import {
    type FC
} from "react";
import {
    Pressable,
    View
} from "react-native";
import type IFileUploadGalleryProps from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    Plus as PlusIcon
} from "lucide-react-native";
import {
    FILE_UPLOAD_STATUS_STATES,
    getFileDescription
} from "../../util";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    formatNumber
} from "../../../../helpers/locale";
import PresenceList from "../../../presenceList";
import Attachment from "../../../attachment";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const FileUploadGallery: FC<IFileUploadGalleryProps> = ({
    isInitialAnimated = false,
    isDisabled = false,
    customLocalize,
    customTheme,
    isShowAdd,
    onRemove,
    onRetry,
    files,
    onAdd,
    title,
    size
}) => {
    const {
        controlHeights,
        shapeConfigs,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        isDisabled
    });

    const {
        container: containerDynamicStyle,
        surface: surfaceDynamicStyle,
        group: groupDynamicStyle
    } = useStyles({
        controlHeights,
        shapeConfigs,
        isDisabled,
        radiuses,
        borders,
        colors,
        spaces
    });

    const contentColor = isDisabled ? colors.system.state.content.disabled.neutral : colors.content.text.mid;

    const renderAdd = () => {
        if(!isShowAdd) {
            return null;
        }

        return <Pressable
            {...interactionHandlers}
            style={[
                stylesheet.container,
                containerDynamicStyle,
                isDisabled ? stylesheet.disabledCursor : undefined
            ]}
            aria-disabled={isDisabled}
            disabled={isDisabled}
            onPress={onAdd}
            role="button"
            key="add"
        >
            <View
                style={[
                    stylesheet.surface,
                    surfaceDynamicStyle,
                    getFocusRingStyle({
                        isFocusVisible,
                        borders,
                        colors
                    })
                ]}
            >
                <StateLayer
                    isHovered={!isDisabled && isHovered}
                    isPressed={!isDisabled && isPressed}
                    borderRadius={radiuses.form}
                    borderWidth={borders.line}
                    customTheme={customTheme}
                    overlayColor="default"
                />
                <PlusIcon
                    color={isDisabled ? colors.system.state.content.disabled.neutral : colors.content.icon.mid}
                    size={iconSizes.large}
                />
                <Text
                    customColor={contentColor}
                    customTheme={customTheme}
                    variant="labelSmallSize"
                    style={stylesheet.text}
                >
                    {localize("file-upload-add-image")}
                </Text>
            </View>
        </Pressable>;
    };

    const count = formatNumber({
        locale: activeLocale,
        value: files.length
    });

    return <View
        aria-label={title ? `${title} (${count})` : localize("attachments-count", [
            count
        ])}
        style={[
            stylesheet.group,
            groupDynamicStyle
        ]}
        role="group"
    >
        <PresenceList
            isInitialAnimated={isInitialAnimated}
            itemStyle={stylesheet.presenceItem}
            customTheme={customTheme}
        >
            {
                files.map((file) => <Attachment
                    uploadState={FILE_UPLOAD_STATUS_STATES[file.status ?? "queued"]}
                    onRemove={isDisabled ? undefined : () => {
                        onRemove(file);
                    }}
                    onRetry={onRetry && !isDisabled ? () => {
                        onRetry(file);
                    } : undefined}
                    description={getFileDescription({
                        locale: activeLocale,
                        file
                    })}
                    customLocalize={customLocalize}
                    source={file.previewUri ? {
                        uri: file.previewUri
                    } : undefined}
                    errorText={file.errorText}
                    customTheme={customTheme}
                    progress={file.progress}
                    orientation="vertical"
                    title={file.name}
                    key={file.key}
                    size={size}
                />)
            }
        </PresenceList>
        {renderAdd()}
    </View>;
};
export default FileUploadGallery;
